import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    CircularProgress,
    Snackbar,
    Divider,
    Stack,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

import {
    getAllWatchlists,
    addStockToWatchlist,
    createWatchlist,
    bulkAddToWatchlist,          // ← NEW
} from "../../../api/watchlist_api";

const AddToWatchlistDialog = ({ open, onClose, ticker, tickers }) => {
    const [watchlists, setWatchlists]         = useState([]);
    const [selectedWatchlist, setSelectedWatchlist] = useState("");
    const [newWatchlistName, setNewWatchlistName]   = useState("");
    const [saving, setSaving]                 = useState(false);
    const [snackbar, setSnackbar]             = useState(null);

    const tickerList = tickers?.length ? tickers : ticker ? [ticker] : [];
    const isBulk     = tickerList.length > 1;       // ← drives which API to call

    /* ── Load watchlists ── */
    useEffect(() => {
        if (!open) return;
        getAllWatchlists().then(res => {
            if (res?.success) setWatchlists(res.data || []);
        });
    }, [open]);

    /* ── Reset on close ── */
    useEffect(() => {
        if (!open) {
            setSelectedWatchlist("");
            setNewWatchlistName("");
        }
    }, [open]);

    /* ─────────────────────────────────────────────
       Core add helper
       • bulk  → POST /watchlists/:id/stocks/batch
       • single → POST /watchlists/:id/stocks  (loop of 1)
    ───────────────────────────────────────────── */
    const addAllTickers = async (watchlistId) => {
        if (isBulk) {
            const stocks = tickerList.map((t) => ({ ticker: t, notes: "" }));
            const res = await bulkAddToWatchlist(watchlistId, stocks);
            if (!res?.success) throw new Error("Bulk add failed");
        } else {
            // single ticker — keep original per-stock endpoint
            for (const t of tickerList) {
                await addStockToWatchlist(watchlistId, { ticker: t });
            }
        }
    };

    /* ── Add to existing watchlist ── */
    const handleAddExisting = async () => {
        if (!selectedWatchlist || tickerList.length === 0) return;
        setSaving(true);
        try {
            await addAllTickers(selectedWatchlist);
            setSnackbar({
                message: isBulk
                    ? `${tickerList.length} stocks added to watchlist`
                    : `${tickerList[0]} added to watchlist`,
            });
            onClose(true);
        } catch (e) {
            console.error(e);
            onClose(false);
        } finally {
            setSaving(false);
        }
    };

    /* ── Create new watchlist then add ── */
    const handleCreateAndAdd = async () => {
        if (!newWatchlistName.trim()) return;
        setSaving(true);
        try {
            const res = await createWatchlist({ name: newWatchlistName.trim() });
            if (res?.success) {
                await addAllTickers(res.data.id);
                setSnackbar({
                    message: isBulk
                        ? `${tickerList.length} stocks added to "${newWatchlistName.trim()}"`
                        : `${tickerList[0]} added to "${newWatchlistName.trim()}"`,
                });
                onClose(true);
            } else {
                onClose(false);
            }
        } catch (e) {
            console.error(e);
            onClose(false);
        } finally {
            setSaving(false);
        }
    };

    const canAddExisting = !!selectedWatchlist && !saving;
    const canCreateNew   = !!newWatchlistName.trim() && !saving;

    return (
        <>
            <Dialog
                open={open}
                onClose={() => onClose(false)}   // ← was `() => onClose` (bug fix)
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    sx: {
                        bgcolor: "var(--color-surface)",
                        backgroundImage: "none",
                        borderRadius: "var(--radius-lg)",
                        border: "1px solid var(--color-border-primary)",
                    },
                }}
            >
                {/* HEADER */}
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid var(--color-border-primary)",
                        py: 1.5,
                        px: 2.5,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.25}>
                        <Box
                            sx={{
                                width: 32, height: 32,
                                borderRadius: 1,
                                bgcolor: "var(--color-surface-hover)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <PlaylistAddIcon sx={{ fontSize: 16, color: "var(--color-primary)" }} />
                        </Box>
                        <Box>
                            <Typography
                                fontSize="var(--font-size-base)"
                                fontWeight="var(--font-weight-semibold)"
                                color="var(--color-text-primary)"
                            >
                                Add to watchlist
                            </Typography>
                            <Typography
                                fontSize="var(--font-size-xs)"
                                color="var(--color-text-secondary)"
                                fontWeight="var(--font-weight-medium)"
                            >
                                {isBulk ? `${tickerList.length} stocks selected` : tickerList[0]}
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton
                        onClick={() => onClose(false)}
                        size="small"
                        sx={{
                            color: "var(--color-text-secondary)",
                            "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-primary)" },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>

                {/* CONTENT */}
                <DialogContent sx={{ py: 2.5, px: 2.5 }}>
                    {watchlists.length > 0 && (
                        <Box mb={2} mt={1.5}>
                            <Typography
                                fontSize="var(--font-size-sm)"
                                fontWeight="var(--font-weight-semibold)"
                                color="var(--color-text-primary)"
                                letterSpacing="0.05em"
                                my={1}
                            >
                                Select Watchlist
                            </Typography>

                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={selectedWatchlist}
                                onChange={(e) => {
                                    setSelectedWatchlist(e.target.value);
                                    setNewWatchlistName("");
                                }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                bgcolor: "var(--color-bg-secondary)",
                                                border: "1px solid var(--color-border-primary)",
                                                "& .MuiMenuItem-root": {
                                                    color: "var(--color-text-primary)",
                                                    fontSize: "var(--font-size-sm)",
                                                },
                                                "& .MuiMenuItem-root:hover": {
                                                    bgcolor: "var(--color-surface-hover)",
                                                },
                                            },
                                        },
                                    },
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        bgcolor: "var(--color-bg-secondary)",
                                        color: "var(--color-text-primary)",
                                        fontSize: "var(--font-size-sm)",
                                        "& fieldset": { borderColor: "var(--color-border-primary)" },
                                        "&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
                                        "&.Mui-focused fieldset": { borderColor: "var(--color-primary)" },
                                    },
                                }}
                            >
                                <MenuItem disabled value="">
                                    Choose watchlist
                                </MenuItem>
                                {watchlists.map(w => (
                                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                                ))}
                            </TextField>

                            <Divider sx={{ my: 1.5, borderColor: "var(--color-border-primary)" }}>
                                <Typography
                                    fontSize="var(--font-size-xs)"
                                    color="var(--color-text-muted)"
                                    fontWeight="var(--font-weight-semibold)"
                                    px={1}
                                >
                                    OR
                                </Typography>
                            </Divider>
                        </Box>
                    )}

                    <Box>
                        <Typography
                            fontSize="var(--font-size-sm)"
                            fontWeight="var(--font-weight-semibold)"
                            color="var(--color-text-primary)"
                            letterSpacing="0.05em"
                            my={1}
                        >
                            Create New Watchlist
                        </Typography>

                        <TextField
                            fullWidth
                            size="small"
                            placeholder="e.g. Tech Stocks, Long Term"
                            value={newWatchlistName}
                            onChange={(e) => {
                                setNewWatchlistName(e.target.value);
                                setSelectedWatchlist("");
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    bgcolor: "var(--color-bg-secondary)",
                                    fontSize: "var(--font-size-sm)",
                                    color: "var(--color-text-primary)",
                                    "& fieldset": { borderColor: "var(--color-border-primary)" },
                                    "&:hover fieldset": { borderColor: "var(--color-border-secondary)" },
                                    "&.Mui-focused fieldset": { borderColor: "var(--color-primary)" },
                                    "& input::placeholder": {
                                        color: "var(--color-text-muted)",
                                        opacity: 1,
                                        fontSize: "var(--font-size-sm)",
                                    },
                                },
                            }}
                        />
                    </Box>
                </DialogContent>

                {/* ACTIONS */}
                <DialogActions
                    sx={{ px: 2.5, pb: 1.5, borderTop: "1px solid var(--color-border-primary)" }}
                >
                    <Button
                        onClick={() => onClose(false)}
                        disabled={saving}
                        sx={{
                            textTransform: "none",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: "var(--font-weight-medium)",
                            color: "var(--color-text-secondary)",
                            "&:hover": { bgcolor: "var(--color-surface-hover)" },
                        }}
                    >
                        Cancel
                    </Button>

                    {canCreateNew ? (
                        <Button
                            variant="contained"
                            onClick={handleCreateAndAdd}
                            disabled={saving}
                            startIcon={
                                saving
                                    ? <CircularProgress size={11} color="inherit" />
                                    : <PlaylistAddIcon sx={{ fontSize: 14 }} />
                            }
                            sx={{
                                textTransform: "none",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: "var(--font-weight-medium)",
                                bgcolor: "var(--color-primary)",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                                "&.Mui-disabled": {
                                    bgcolor: "var(--color-surface-hover)",
                                    color: "var(--color-text-secondary)",
                                },
                            }}
                        >
                            Create & Add
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={!canAddExisting}
                            onClick={handleAddExisting}
                            startIcon={
                                saving
                                    ? <CircularProgress size={11} color="inherit" />
                                    : <PlaylistAddIcon sx={{ fontSize: 14 }} />
                            }
                            sx={{
                                textTransform: "none",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: "var(--font-weight-medium)",
                                bgcolor: "var(--color-primary)",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                                "&.Mui-disabled": {
                                    bgcolor: "var(--color-surface-hover)",
                                    color: "var(--color-text-secondary)",
                                },
                            }}
                        >
                            Add to Watchlist
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* SNACKBAR */}
            <Snackbar
                open={!!snackbar}
                autoHideDuration={3000}
                message={
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Box
                            sx={{
                                width: 18, height: 18,
                                borderRadius: "50%",
                                bgcolor: "var(--color-success)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 11, color: "#fff",
                            }}
                        >
                            ✓
                        </Box>
                        {snackbar?.message}
                    </Stack>
                }
                onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                ContentProps={{
                    sx: {
                        fontSize: "var(--font-size-xs)",
                        bgcolor: "var(--color-surface)",
                        color: "var(--color-text-primary)",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--color-border-primary)",
                        minWidth: 0,
                    },
                }}
            />
        </>
    );
};

export default AddToWatchlistDialog;